import { escapeHtml } from '../utils/html_utils.js?v=20260706d';

let workspaceLoadingSeq = 0;
let activeWorkspaceLoadingOverlay = null;
let workspaceLoadingCleanupBound = false;
const renderingLoads = new Set();
let hideRequested = false;

function workspaceLoadingMarkHtml() {
  return `
    <div class="three-d-loading-mark" aria-hidden="true">
      <span></span><span></span><span></span>
      <span></span><span></span><span></span>
      <span></span><span></span><span></span>
    </div>
  `;
}

function hideWorkspaceLoadingOverlay(token = null) {
  if (token !== null && token !== workspaceLoadingSeq) return;
  hideRequested = true;
  if (renderingLoads.size) return;
  activeWorkspaceLoadingOverlay?.remove();
  activeWorkspaceLoadingOverlay = null;
}

function ensureWorkspaceLoadingCleanup() {
  if (workspaceLoadingCleanupBound || typeof window === 'undefined') return;
  workspaceLoadingCleanupBound = true;
  window.addEventListener('pagehide', () => {
    renderingLoads.clear();
    hideWorkspaceLoadingOverlay();
  });
}

function showWorkspaceLoadingOverlay({
  label = 'Daten werden geladen...',
  details = 'Daten werden vorbereitet',
  allowDialogs = false
} = {}) {
  ensureWorkspaceLoadingCleanup();
  const token = ++workspaceLoadingSeq;
  activeWorkspaceLoadingOverlay?.remove();
  hideRequested = false;
  const overlay = document.createElement('div');
  overlay.className = allowDialogs
    ? 'order-loading-overlay order-loading-overlay--allow-dialogs'
    : 'order-loading-overlay';
  overlay.dataset.orderLoadingToken = String(token);
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.innerHTML = `
    <div class="order-loading-card">
      ${workspaceLoadingMarkHtml()}
      <div class="three-d-loading-label">${escapeHtml(label)}</div>
      <div class="three-d-loading-progress" aria-hidden="true"><span class="three-d-loading-progress-bar"></span></div>
      <div class="three-d-loading-detail">${escapeHtml(details)}</div>
    </div>
  `;
  document.body.appendChild(overlay);
  activeWorkspaceLoadingOverlay = overlay;
  return token;
}

// Rendering outlives the data load. Keep its overlay until both owners finish;
// a newer order overlay must not be removed by an older load's completion.
function retainWorkspaceLoadingForRendering() {
  if (!activeWorkspaceLoadingOverlay) {
    showWorkspaceLoadingOverlay({
      label: 'Darstellung wird geladen...',
      details: 'Die Zeichnung wird vorbereitet'
    });
    hideRequested = true;
  }
  const hold = {};
  renderingLoads.add(hold);
  return () => {
    if (!renderingLoads.delete(hold)) return;
    if (!renderingLoads.size && hideRequested) hideWorkspaceLoadingOverlay();
  };
}

function afterWorkspaceLoadingPaint() {
  if (typeof requestAnimationFrame !== 'function') return Promise.resolve();
  return new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

async function withWorkspaceLoadingOverlay(task, options = {}) {
  const token = showWorkspaceLoadingOverlay(options);
  try {
    // Let the existing logo and progress bar paint before synchronous setup.
    await afterWorkspaceLoadingPaint();
    const result = await task();
    await afterWorkspaceLoadingPaint();
    return result;
  } finally {
    hideWorkspaceLoadingOverlay(token);
  }
}

export {
  retainWorkspaceLoadingForRendering,
  hideWorkspaceLoadingOverlay,
  showWorkspaceLoadingOverlay,
  withWorkspaceLoadingOverlay
};
